/**
 * Owns the save-time override context: folds each settled availability
 * window into a session-scoped snapshot and pushes it to the parent.
 *
 * Split out of `CalendarPanel` so the accumulation rules — especially
 * the reset on roster/timezone change, which would otherwise classify
 * slots against a different interviewer's hours — are unit-testable
 * without mounting FullCalendar. See `accumulateScheduleContext.ts`
 * for why the accumulation exists at all (ENG-3571).
 */
import { useEffect, useRef } from 'react';
import type { BusyEvent } from '../../availability';
import {
  EMPTY_SCHEDULE_CONTEXT,
  accumulateScheduleContext,
  type ScheduleContextSnapshot,
  type ScheduleContextWeek,
} from './accumulateScheduleContext';

export interface ScheduleContextEmission {
  workingHoursByInterviewer: ScheduleContextSnapshot['workingHoursByInterviewer'];
  busyEvents: ScheduleContextSnapshot['busyEvents'];
  coveredRanges: ScheduleContextSnapshot['coveredRanges'];
  isReady: boolean;
}

export interface UseScheduleContextEmitterArgs {
  /** Self-schedule modes only — manual mode has no slot classification. */
  enabled: boolean;
  /**
   * Everything the accumulation is only valid for: the interviewer
   * roster and the timezone. Changing it drops the snapshot.
   */
  resetKey: string;
  /** Weeks whose working-hours fetch resolved. */
  weeks: ReadonlyArray<ScheduleContextWeek>;
  busyEvents: ReadonlyArray<BusyEvent>;
  /** Both feeds have settled — see `CalendarPanel`'s `isContextReady`. */
  isReady: boolean;
  onChange?: (_ctx: ScheduleContextEmission) => void;
}

export function useScheduleContextEmitter({
  enabled,
  resetKey,
  weeks,
  busyEvents,
  isReady,
  onChange,
}: UseScheduleContextEmitterArgs): void {
  const storeRef = useRef<{
    key: string;
    snapshot: ScheduleContextSnapshot;
    emittedReady: boolean | null;
  }>({
    key: resetKey,
    snapshot: EMPTY_SCHEDULE_CONTEXT,
    emittedReady: null,
  });

  useEffect(() => {
    if (!onChange || !enabled) return;
    const store = storeRef.current;
    if (store.key !== resetKey) {
      store.key = resetKey;
      store.snapshot = EMPTY_SCHEDULE_CONTEXT;
      store.emittedReady = null;
    }
    // Only record a window as covered once the busy feed has settled
    // too — a week marked covered while its events are still in flight
    // would classify a slot sitting on a busy event as clear.
    const next = isReady
      ? accumulateScheduleContext(store.snapshot, { weeks, busyEvents })
      : store.snapshot;
    if (next === store.snapshot && store.emittedReady === isReady) return;
    store.snapshot = next;
    store.emittedReady = isReady;
    onChange({
      workingHoursByInterviewer: next.workingHoursByInterviewer,
      busyEvents: next.busyEvents,
      coveredRanges: next.coveredRanges,
      isReady,
    });
  }, [enabled, resetKey, weeks, busyEvents, isReady, onChange]);
}
